import type { SupabaseClient } from "@supabase/supabase-js";

export const THEME_SCHEDULE_KEY = "theme_schedule";

export type ThemeSchedule = {
  /** Timed auto-activation: scheduled themes go live within their dates. */
  enabled: boolean;
  /** Admin override: pauses the calendar and locks override_slug live. */
  override: boolean;
  override_slug: string | null;
};

export const DEFAULT_SCHEDULE: ThemeSchedule = { enabled: false, override: false, override_slug: null };

/** Recommended yearly windows (MM-DD) for seasonal presets, matched by slug prefix. */
export const SEASON_WINDOWS: { prefix: string; label: string; start: string; end: string }[] = [
  { prefix: "memorial-day", label: "Memorial Day", start: "05-15", end: "05-31" },
  { prefix: "fourth-of-july", label: "4th of July", start: "06-25", end: "07-07" },
  { prefix: "fall-clearance", label: "Fall Clearance", start: "09-15", end: "11-15" },
  { prefix: "holiday-season", label: "Holiday Season", start: "11-20", end: "12-31" },
];

export type LiveTheme = { slug: string; name: string; layout: string; tokens: Record<string, string> };

/**
 * Which theme visitors should see right now:
 * override (if on) → scheduled theme whose dates include today (if timed on) →
 * any active non-default theme → null (Rex & Sons Classic).
 */
export async function resolveLiveTheme(supabase: SupabaseClient): Promise<LiveTheme | null> {
  // One parallel round trip (schedule + the small themes table) instead of
  // 2–3 sequential reads; the first page load waits on this.
  const cols = "slug,name,layout,tokens,is_default,status,start_date,end_date";
  const [{ data: s }, { data: rows }] = await Promise.all([
    supabase.from("site_settings").select("value").eq("key", THEME_SCHEDULE_KEY).maybeSingle(),
    supabase.from("site_themes").select(cols),
  ]);
  return pickLiveTheme({ ...DEFAULT_SCHEDULE, ...((s?.value as Partial<ThemeSchedule>) ?? {}) }, (rows ?? []) as ThemeRow[]);
}

export type ThemeRow = LiveTheme & { is_default?: boolean; status?: string; start_date?: string | null; end_date?: string | null };

/** Pure resolver: same precedence as before, applied to already-loaded rows. */
export function pickLiveTheme(sched: ThemeSchedule, rows: ThemeRow[], today = new Date().toISOString().slice(0, 10)): LiveTheme | null {
  if (sched.override && sched.override_slug) {
    const t = rows.find((r) => r.slug === sched.override_slug);
    if (t) return t.is_default ? null : t;
  }
  if (sched.enabled) {
    const t = rows
      .filter((r) => r.status === "scheduled" && r.start_date && r.end_date && r.start_date <= today && r.end_date >= today)
      .sort((a, b) => String(b.start_date).localeCompare(String(a.start_date)))[0];
    if (t) return t;
  }
  return rows.find((r) => r.status === "active" && !r.is_default) ?? null;
}
